import React, {useEffect, useCallback, useState, useRef} from 'react';
import {CurrenciesDropdown} from '../CurrenciesDropdown/CurrenciesDropdown';
import {IPropsInjectedByViewerScript} from '../../viewerScript/currencyConverterStore';
import {isExperimentEnabled} from '../../utils/experiments.utility';
import {FEATURE_FLAGS} from '../../../settings/constants/featureFlags';
import {AutoSwitchToast, AutoSwitchToastPhase} from '../AutoSwitchToast/AutoSwitchToast';

const TOAST_PHASE_DURATION = 5000;

export const CurrencyConverterApp = (props: IPropsInjectedByViewerScript) => {
  const {
    userCurrencies,
    currencySymbols,
    selectedCurrency,
    onCurrencySelected,
    translations,
    isRtl,
    isMobile,
    host,
    onAppLoaded,
    reportAppNotLoaded,
    experiments,
    suggestedCurrencyCode,
    suggestedCurrencyAutoSwitchEnabled,
    shouldShowAutoSwitchToast,
    biParams,
    queryCurrency,
    onBIEvent,
  } = props;

  const [autoSwitchedTo, setAutoSwitchedTo] = useState<string | null>(null);
  const [autoSwitchToastPhase, setAutoSwitchToastPhase] = useState<AutoSwitchToastPhase>('loading');
  const [previousCurrency, setPreviousCurrency] = useState<string | null>(null);
  const initialCurrencyRef = useRef<string | null>(selectedCurrency);
  const autoSwitchAppliedCallbackRef = useRef<(() => void) | null>(null);

  const handleAutoSwitched = useCallback(
    (code: string, onSwitchApplied: () => void) => {
      if (!shouldShowAutoSwitchToast) {
        onCurrencySelected(code);
        onSwitchApplied();
        return;
      }

      setPreviousCurrency(initialCurrencyRef.current);
      autoSwitchAppliedCallbackRef.current = onSwitchApplied;
      setAutoSwitchToastPhase('loading');
      setAutoSwitchedTo(code);
    },
    [onCurrencySelected, shouldShowAutoSwitchToast]
  );

  const handleSwitchBack = useCallback(() => {
    if (previousCurrency) {
      onCurrencySelected(previousCurrency);
    }
    setAutoSwitchedTo(null);
    setAutoSwitchToastPhase('loading');
    autoSwitchAppliedCallbackRef.current = null;
  }, [previousCurrency, onCurrencySelected]);

  const handleCloseToast = useCallback(() => {
    setAutoSwitchedTo(null);
    setAutoSwitchToastPhase('loading');
    autoSwitchAppliedCallbackRef.current = null;
  }, []);

  useEffect(() => {
    if (!autoSwitchedTo) {
      return;
    }

    if (autoSwitchToastPhase === 'loading') {
      const id = setTimeout(() => {
        onCurrencySelected(autoSwitchedTo);
        autoSwitchAppliedCallbackRef.current?.();
        autoSwitchAppliedCallbackRef.current = null;
        setAutoSwitchToastPhase('undo');
      }, TOAST_PHASE_DURATION);
      return () => clearTimeout(id);
    }

    const id = setTimeout(handleCloseToast, TOAST_PHASE_DURATION);
    return () => clearTimeout(id);
  }, [autoSwitchedTo, autoSwitchToastPhase, handleCloseToast, onCurrencySelected]);

  const reportAppLoaded = useCallback(() => {
    /* istanbul ignore next: hard to test it */
    if (selectedCurrency && typeof onAppLoaded === 'function') {
      onAppLoaded();
    } else {
      reportAppNotLoaded(selectedCurrency, onAppLoaded);
    }
  }, [selectedCurrency, onAppLoaded, reportAppNotLoaded]);

  useEffect(() => {
    host.registerToComponentDidLayout(reportAppLoaded);
  }, [host, reportAppLoaded]);

  const isPopoverWithSuggestCurrencyEnabled = isExperimentEnabled(
    experiments,
    FEATURE_FLAGS.suggestLocalCurrencyForConverter
  );
  const useFlagIconInSuggestionPopover = isExperimentEnabled(
    experiments,
    FEATURE_FLAGS.useCurrencyConverterFlagIconsInSuggestionPopover
  );

  const shouldShowSuggestCurrency =
    props.shouldShowSuggestCurrency ??
    props?.host?.style?.styleParams.booleans.currencyConverter_shouldShowSuggestCurrency ??
    true;

  const shouldUseAutoCurrencySwitch =
    props.shouldUseAutoCurrencySwitch ??
    props?.host?.style?.styleParams.booleans.currencyConverter_shouldUseAutoCurrencySwitch ??
    true;

  return (
    <>
      <CurrenciesDropdown
        shouldShowSuggestCurrency={shouldShowSuggestCurrency && isPopoverWithSuggestCurrencyEnabled}
        shouldUseAutoCurrencySwitch={shouldUseAutoCurrencySwitch}
        currencySymbols={currencySymbols}
        currencies={userCurrencies}
        onChange={onCurrencySelected}
        queryCurrency={queryCurrency}
        selectedCurrency={selectedCurrency}
        translations={translations}
        isRtl={isRtl}
        isMobile={isMobile}
        suggestedCurrencyCode={suggestedCurrencyCode}
        suggestedCurrencyAutoSwitchEnabled={suggestedCurrencyAutoSwitchEnabled}
        useFlagIconInSuggestionPopover={useFlagIconInSuggestionPopover}
        biParams={biParams}
        onBIEvent={onBIEvent}
        onAutoSwitched={handleAutoSwitched}
      />
      {shouldShowAutoSwitchToast && (
        <AutoSwitchToast
          currency={autoSwitchedTo}
          phase={autoSwitchToastPhase}
          previousCurrency={previousCurrency}
          translations={translations}
          onClose={handleCloseToast}
          onUndo={handleSwitchBack}
        />
      )}
    </>
  );
};
